import React, { useMemo, useEffect, useRef, useState } from "react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import { LockFill } from "react-bootstrap-icons";
import BootstrapModal from "../shared/BootstrapModal";

/* -------------------- Shared Card styling -------------------- */
const cardStyle = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#9e2146" },
  },
};

const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);

  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_term: params.get("utm_term") || "",
    utm_content: params.get("utm_content") || "",
  };
};

/* -------------------- Payment helpers -------------------- */
const createCheckoutIntent = async ({
  store_slug,
  price_slug,
  gateway_id,

  // ✅ optional now (no-card flow)
  payment_method_id,

  // customer + billing
  name,
  email,
  country,
  country_iso2,
  state,
  postal_code,
  phone_code,
  phone_number,
  authorized,

  // marketing
  utm_params,

  // coupon
  coupon_code,

  // user agent
  authorized_at,
  user_agent,

  // For Everflow
  ef_transaction_id,
  ef_affid,
  ef_oid,
}) => {
  const endpoint_url = `${process.env.REACT_APP_API_ENDPOINT}/api/v2/stores/${store_slug}/price-plans/${price_slug}/payment-processing`;

  const url = new URL(endpoint_url);

  if (coupon_code) url.searchParams.set("coupon_code", coupon_code);

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Cache-Control": "no-store",
    },
    body: JSON.stringify({
      payment_account_id: gateway_id,
      payment_method_id: payment_method_id || undefined,

      name,
      email,
      country,
      country_iso2,
      state,
      postal_code,
      phone_code,
      phone_number,
      authorized,

      coupon_code: coupon_code || "",

      ...utm_params,

      authorized_at,
      user_agent,

      ef_transaction_id,
      ef_affid,
      ef_oid,
    }),
  });

  const data = await res.json();

  if (!res.ok) {
    const msg =
      data?.error?.message ||
      data?.errors?.[0]?.message ||
      `Checkout failed (${res.status})`;

    const err = new Error(msg);
    err.status = res.status;
    err.errors = Array.isArray(data?.errors) ? data.errors : [];
    err.raw = data;
    throw err;
  }

  return data?.payloads ?? null;
};

const StripePaymentElements = ({
  store_slug,
  price_slug,
  gateway_id,

  email,
  fullName,
  selectedCountry,
  selectedState,
  stateOptions,
  postalCode,
  dialingCode,
  contactNumber,
  agreementAccepted,
  couponCode,

  // ✅ if true: show only button, no card fields, no stripe PM
  freeTrialNoCard,

  // For Everflow
  ef_transaction_id,
  ef_affid,
  ef_oid,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const PERMISSION_ERROR_TYPES = useMemo(
    () => ({
      PRICE_PLAN: "ERR_PRICE_PLAN_PERMISSION",
      PRICE_PLAN_GENERAL: "ERR_PRICE_PLAN_GENERAL",
      COUPON: "ERR_COUPON_PERMISSION",
    }),
    [],
  );

  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionErrors, setPermissionErrors] = useState([]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [cardError, setCardError] = useState("");
  const [appErrors, setAppErrors] = useState([]);

  const [successCountdown, setSuccessCountdown] = useState(null);
  const redirectTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearInterval(redirectTimerRef.current);
    };
  }, []);

  const startRedirectCountdown = (redirectUrl) => {
    if (!redirectUrl) return;

    if (redirectTimerRef.current) clearInterval(redirectTimerRef.current);

    setSuccessCountdown(5);

    let seconds = 5;

    redirectTimerRef.current = setInterval(() => {
      seconds -= 1;
      setSuccessCountdown(seconds);

      if (seconds <= 0) {
        clearInterval(redirectTimerRef.current);
        redirectTimerRef.current = null;
        window.location.href = redirectUrl;
      }
    }, 1000);
  };

  const onStripeChange = (e) => {
    setCardError(e?.error?.message || "");
  };

  const closePermissionModal = () => {
    setShowPermissionModal(false);
  };

  const permissionGroups = useMemo(() => {
    const groups = { pricePlan: [], coupon: [] };

    for (const e of permissionErrors) {
      const code = String(e?.code || "");

      if (
        code === PERMISSION_ERROR_TYPES.PRICE_PLAN ||
        code === PERMISSION_ERROR_TYPES.PRICE_PLAN_GENERAL
      ) {
        groups.pricePlan.push(e);
      }

      if (code === PERMISSION_ERROR_TYPES.COUPON) {
        groups.coupon.push(e);
      }
    }

    return groups;
  }, [permissionErrors, PERMISSION_ERROR_TYPES]);

  const permissionModalType = useMemo(() => {
    if (permissionGroups.pricePlan.length > 0) return "PRICE_PLAN";
    if (permissionGroups.coupon.length > 0) return "COUPON";
    return null;
  }, [permissionGroups]);

  const permissionModalConfig = useMemo(() => {
    const isPricePlan = permissionModalType === "PRICE_PLAN";
    const isCoupon = permissionModalType === "COUPON";

    const cfg = {
      title: "",
      onClose: closePermissionModal,
      footer: (
        <button
          type='button'
          className='btn btn-lg btn-success'
          onClick={closePermissionModal}
        >
          Okay
        </button>
      ),
      intro: null,
    };

    if (isCoupon) {
      cfg.intro = (
        <p className='mb-3'>
          This coupon code cannot be applied to the following items due to
          eligibility requirements.
        </p>
      );
      return cfg;
    }

    if (isPricePlan) {
      cfg.onClose = undefined;
      cfg.footer = null;
      cfg.intro = <></>;
      return cfg;
    }

    return cfg;
  }, [permissionModalType]);

  const validateFront = () => {
    const errors = [];

    const countryName = String(selectedCountry?.name || "").trim();
    const countryIso2 = String(selectedCountry?.iso2 || "").toUpperCase();

    if (!String(fullName || "").trim()) errors.push("Full name is required.");
    if (!String(email || "").trim()) errors.push("Email is required.");
    if (!countryName) errors.push("Country / Region is required.");
    if (!countryIso2) errors.push("Country ISO2 is required.");
    if (!String(dialingCode || "").trim())
      errors.push("Dialing code is required.");
    if (!String(contactNumber || "").trim())
      errors.push("Contact number is required.");

    const stateRequired =
      countryIso2 &&
      countryIso2 !== "SG" &&
      Array.isArray(stateOptions) &&
      stateOptions.length > 0;

    if (stateRequired && !String(selectedState?.name || "").trim()) {
      errors.push("State is required.");
    }

    if (countryIso2 === "US" || countryIso2 === "CA") {
      const v = String(postalCode || "").trim();
      if (!v) errors.push("ZIP / Postal Code is required.");
      else if (v.length < 3)
        errors.push("ZIP / Postal Code must be at least 3 characters.");
    }

    if (!agreementAccepted) {
      errors.push(
        "You must agree to the terms before completing your purchase.",
      );
    }

    return errors;
  };

  const submitPayment = async (e) => {
    e.preventDefault();

    setCardError("");
    setAppErrors([]);

    // ✅ even for no-card flow, we still need stripe context loaded (since you’re under Stripe gateway),
    // but we do NOT call stripe.createPaymentMethod.
    if (!stripe) return;

    const errors = validateFront();
    if (errors.length) {
      setAppErrors(errors);
      return;
    }

    setIsProcessing(true);

    try {
      const utm_params = getUtmParams();

      // ✅ free trial / no card required:
      // - call backend without payment_method_id
      // - backend may return no checkout_intent
      if (freeTrialNoCard) {
        const payloads = await createCheckoutIntent({
          store_slug,
          price_slug,
          gateway_id,
          payment_method_id: undefined,

          name: fullName,
          email,

          country: selectedCountry?.name || "",
          country_iso2: selectedCountry?.iso2 || "",
          state: selectedState?.name || "",
          postal_code: String(postalCode || "")
            .trim()
            .toUpperCase(),

          phone_code: String(dialingCode || "").trim(),
          phone_number: String(contactNumber || "").trim(),

          utm_params,
          coupon_code: couponCode,
          authorized: agreementAccepted,
          authorized_at: new Date().toISOString(),
          user_agent: window.navigator.userAgent,

          ef_transaction_id,
          ef_affid,
          ef_oid,
        });

        const upsells = Array.isArray(payloads?.upsell_redirects)
          ? payloads.upsell_redirects
          : [];
        const firstUpsell = upsells.find((u) => u?.url);
        const thankYouUrl = payloads?.thank_you_redirect?.url;

        const redirectUrl = firstUpsell?.url || thankYouUrl || "";
        startRedirectCountdown(redirectUrl);
        return;
      }

      // ✅ normal card-required flow
      if (!elements) return;

      const cardNumberEl = elements.getElement(CardNumberElement);
      if (!cardNumberEl) return;

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberEl,
      });

      if (error) {
        setCardError(error.message);
        return;
      }

      const payloads = await createCheckoutIntent({
        store_slug,
        price_slug,
        gateway_id,
        payment_method_id: paymentMethod.id,

        name: fullName,
        email,

        country: selectedCountry?.name || "",
        country_iso2: selectedCountry?.iso2 || "",
        state: selectedState?.name || "",
        postal_code: String(postalCode || "")
          .trim()
          .toUpperCase(),

        phone_code: String(dialingCode || "").trim(),
        phone_number: String(contactNumber || "").trim(),

        utm_params,
        coupon_code: couponCode,
        authorized: agreementAccepted,

        authorized_at: new Date().toISOString(),
        user_agent: window.navigator.userAgent,

        ef_transaction_id,
        ef_affid,
        ef_oid,
      });

      const checkout_intent = payloads?.checkout_intent;

      if (!checkout_intent?.client_secret || !checkout_intent?.object) {
        throw new Error("Backend did not return a valid checkout intent.");
      }

      if (checkout_intent.object === "payment_intent") {
        const { error: confirmErr, paymentIntent } =
          await stripe.confirmCardPayment(checkout_intent.client_secret, {
            payment_method: paymentMethod.id,
          });

        if (confirmErr)
          throw new Error(confirmErr.message || "Payment failed.");
        if (paymentIntent?.status !== "succeeded") {
          throw new Error(
            `Payment not completed (status: ${paymentIntent?.status})`,
          );
        }
      } else if (checkout_intent.object === "setup_intent") {
        const { error: confirmErr, setupIntent } =
          await stripe.confirmCardSetup(checkout_intent.client_secret, {
            payment_method: paymentMethod.id,
          });

        if (confirmErr)
          throw new Error(confirmErr.message || "Card setup failed.");

        if (setupIntent?.status !== "succeeded") {
          throw new Error(
            `Card setup not completed (status: ${setupIntent?.status})`,
          );
        }
      } else {
        throw new Error(
          `Unsupported checkout intent object: ${checkout_intent.object}`,
        );
      }

      const upsells = Array.isArray(payloads?.upsell_redirects)
        ? payloads.upsell_redirects
        : [];

      const firstUpsell = upsells.find((u) => u?.url);
      const thankYouUrl = payloads?.thank_you_redirect?.url;

      const redirectUrl = firstUpsell?.url || thankYouUrl || "";
      startRedirectCountdown(redirectUrl);
      return;
    } catch (err) {
      const all = Array.isArray(err?.errors) ? err.errors : [];

      const permissionList = all.filter((e) => {
        const code = String(e?.code || "");
        return (
          code === PERMISSION_ERROR_TYPES.PRICE_PLAN ||
          code === PERMISSION_ERROR_TYPES.PRICE_PLAN_GENERAL ||
          code === PERMISSION_ERROR_TYPES.COUPON
        );
      });

      if (permissionList.length > 0) {
        setPermissionErrors(permissionList);
        setShowPermissionModal(true);
        return;
      }

      setAppErrors([err?.message || "Unexpected error"]);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <form onSubmit={submitPayment}>
        {/* ✅ If freeTrialNoCard: hide all card inputs, show only button */}
        {!freeTrialNoCard && (
          <>
            <div className='fw-bold text-start'>
              <p className='m-0 text-uppercase'>Card Number</p>
              <div className='p-3 mb-3 border rounded-3 bg-white'>
                <CardNumberElement
                  options={cardStyle}
                  onChange={onStripeChange}
                />
              </div>
            </div>

            <div className='row'>
              <div className='col-md-6 fw-bold text-start'>
                <p className='m-0 text-uppercase'>Expiry Date</p>
                <div className='p-3 mb-3 border rounded-3 bg-white'>
                  <CardExpiryElement
                    options={cardStyle}
                    onChange={onStripeChange}
                  />
                </div>
              </div>

              <div className='col-md-6 fw-bold text-start'>
                <p className='m-0 text-uppercase'>Security Code</p>
                <div className='p-3 mb-3 border rounded-3 bg-white'>
                  <CardCvcElement
                    options={cardStyle}
                    onChange={onStripeChange}
                  />
                </div>
              </div>
            </div>

            {cardError && <p className='text-danger fw-bold'>{cardError}</p>}
          </>
        )}

        <button
          type='submit'
          className='btn btn-lg fw-bold rounded-pill text-white w-100'
          style={{ backgroundColor: "#3CAD66" }}
          disabled={isProcessing || typeof successCountdown === "number"}
        >
          {isProcessing ? "Processing..." : "Submit"}
        </button>

        <div className='text-muted text-center mt-3 d-flex justify-content-center align-items-center gap-1'>
          <LockFill />
          <span>Encrypted & secure</span>
        </div>

        {Array.isArray(appErrors) && appErrors.length > 0 && (
          <div className='piranha-error-box mt-3'>
            <div className='piranha-error-title'>Please fix the following:</div>
            <ul className='m-0 ps-3'>
              {appErrors.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          </div>
        )}

        {typeof successCountdown === "number" && (
          <div className='mt-3 piranha-success-box'>
            Payment successful. Redirecting in{" "}
            <span className='piranha-success-count'>{successCountdown}</span>{" "}
            {successCountdown === 1 ? "second" : "seconds"}…
          </div>
        )}
      </form>

      <BootstrapModal
        show={showPermissionModal}
        title={permissionModalConfig.title}
        onClose={permissionModalConfig.onClose}
        staticBackdrop
        keyboard={false}
        dialogClassName='modal-lg modal-dialog-centered'
        footer={permissionModalConfig.footer}
      >
        {permissionModalConfig.intro}

        {permissionModalType === "PRICE_PLAN" && (
          <div className='vstack gap-3'>
            {permissionGroups.pricePlan.map((e, idx) => (
              <div key={`price-${e?.code}-${e?.price_id || idx}`}>
                {e?.message && (
                  <div
                    dangerouslySetInnerHTML={{ __html: String(e.message) }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {permissionModalType === "COUPON" && (
          <div className='list-group'>
            {permissionGroups.coupon.map((e, idx) => (
              <div
                key={`coupon-${e?.code}-${e?.price_id || idx}`}
                className='list-group-item'
              >
                <div className='fw-bold'>
                  {idx + 1}. {e?.price_name || "Item"}
                </div>

                {e?.message && (
                  <div
                    className='text-muted mt-2'
                    dangerouslySetInnerHTML={{ __html: String(e.message) }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </BootstrapModal>
    </>
  );
};

export default StripePaymentElements;
